import { watch, nextTick } from 'vue'
import { useWindowScroll, useElementSize } from '@vueuse/core'

let instance = null

const queryModalParams = new Map()

export function registerQueryModalParam(paramName) {
  const registrationCount = queryModalParams.get(paramName) ?? 0
  queryModalParams.set(paramName, registrationCount + 1)
}

export function unregisterQueryModalParam(paramName) {
  const registrationCount = queryModalParams.get(paramName) ?? 0

  if (registrationCount <= 1) {
    queryModalParams.delete(paramName)
    return
  }

  queryModalParams.set(paramName, registrationCount - 1)
}

function isQueryModalNavigation(to, from) {
  if (!to?.path || !from?.path || to.path !== from.path) return false

  const toQuery = to.query ?? {}
  const fromQuery = from.query ?? {}
  const queryKeys = new Set([...Object.keys(toQuery), ...Object.keys(fromQuery)])
  const changedQueryKeys = [...queryKeys].filter(
    (key) => JSON.stringify(toQuery[key]) !== JSON.stringify(fromQuery[key])
  )

  return changedQueryKeys.length > 0 && changedQueryKeys.every((key) => queryModalParams.has(key))
}

export function routeScrollBehavior(to, from, savedPosition) {
  if (isQueryModalNavigation(to, from)) {
    return false
  }

  if (savedPosition) {
    return savedPosition
  }

  return { top: 0 }
}

class RouteHistorySingleton {
  constructor() {
    if (instance) {
      return instance
    }
    this.routeHistory = []
    this.scrollMap = new Map()
    this.maxLength = 10
    instance = this
  }

  pushRoute(route, scrollY = undefined) {
    if (!route || !route.fullPath) return

    const last = this.routeHistory[this.routeHistory.length - 1]
    if (!last || last.fullPath !== route.fullPath) {
      this.routeHistory.push(route)
      if (this.routeHistory.length > this.maxLength) {
        this.routeHistory.shift()
      }
    }

    if (typeof scrollY === 'number') {
      this.scrollMap.set(route.fullPath, scrollY)
    }
  }

  setScroll(fullPath, scrollY) {
    if (!fullPath) return
    this.scrollMap.set(fullPath, scrollY)
  }

  getScroll(fullPath) {
    return this.scrollMap.get(fullPath) || 0
  }

  last() {
    return this.routeHistory[this.routeHistory.length - 1]
  }

  pop() {
    return this.routeHistory.pop()
  }

  getAll() {
    return this.routeHistory.slice()
  }

  clear() {
    this.routeHistory = []
    this.scrollMap.clear()
  }
}

export const routeHistorySingleton = new RouteHistorySingleton()

export const componentState = {
  sectionStatus: null,
  scrollY: 0,
  listOptions: {}
}

export function saveState(sectionVisible, y, options) {
  componentState.sectionStatus = sectionVisible
  componentState.scrollY = y
  componentState.listOptions = options
}

export function getState() {
  return { ...componentState }
}

export function restoreScrollWithUseScroll(targetY, opts = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {}

  const { timeoutMs = 7000, pollInterval = 150, tolerance = 2 } = opts

  const { y: scrollY } = useWindowScroll()
  const body = document.body
  const { height: bodyHeight } = useElementSize(body)

  let intervalId = null
  let timeoutId = null
  const imageListeners = new Map()
  let stopped = false

  const cleanup = () => {
    if (stopped) return
    stopped = true

    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }

    imageListeners.forEach((handler, img) => {
      try {
        img.removeEventListener('load', handler)
        img.removeEventListener('error', handler)
      } catch (e) {}
    })
    imageListeners.clear()
  }

  const clampAndScroll = (y) => {
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    const final = Math.min(y, maxScroll)
    window.scrollTo({ top: final, behavior: 'auto' })
    return final
  }

  const succeeded = () => {
    const cur = window.scrollY || window.pageYOffset || 0
    return (
      Math.abs(cur - targetY) <= tolerance ||
      document.documentElement.scrollHeight - window.innerHeight >= targetY
    )
  }

  nextTick(() => {
    clampAndScroll(targetY)
  })

  if (succeeded()) {
    cleanup()
    return cleanup
  }

  const stopWatch = watch(
    () => bodyHeight.value,
    () => {
      if (stopped) return
      clampAndScroll(targetY)
      if (succeeded()) {
        stopWatch()
        cleanup()
      }
    }
  )

  intervalId = setInterval(() => {
    if (stopped) return
    clampAndScroll(targetY)
    if (succeeded()) {
      clearInterval(intervalId)
      intervalId = null
      stopWatch()
      cleanup()
    }
  }, pollInterval)

  const imgs = Array.from(document.querySelectorAll('img'))
  imgs.forEach((img) => {
    if (!img.complete) {
      const handler = () => {
        if (stopped) return
        clampAndScroll(targetY)
        if (succeeded()) {
          stopWatch()
          cleanup()
        }
      }
      imageListeners.set(img, handler)
      img.addEventListener('load', handler, { passive: true })
      img.addEventListener('error', handler, { passive: true })
    }
  })

  timeoutId = setTimeout(() => {
    if (stopped) return
    clampAndScroll(targetY)
    stopWatch()
    cleanup()
  }, timeoutMs)

  return () => {
    stopWatch()
    cleanup()
  }
}

export function initRouting({ router, app, settings, User }) {
  const DEFAULT_TITLE = settings.PRODUCT_TITLE

  router.beforeEach((to, from, next) => {
    document.title = DEFAULT_TITLE
    if (to.meta.title) {
      document.title = to.meta.title
    }

    if (from && from.fullPath) {
      routeHistorySingleton.pushRoute(from, typeof window !== 'undefined' ? window.scrollY || 0 : 0)
    }

    routeHistorySingleton.pushRoute(from)
    const { $cookies } = app.config.globalProperties

    if (to.name == 'Login' && $cookies.get('token')) {
      next({ name: 'Home' })
      return
    }

    if (!((to.meta ?? { needAuth: true }).needAuth ?? true)) {
      next()
    } else {
      if ($cookies.get('token') == null) next({ name: 'Login', query: { redirect: to.fullPath } })
      else next()
    }
  })

  router.afterEach((to, from) => {
    if (isQueryModalNavigation(to, from)) {
      return
    }

    const hist = routeHistorySingleton.routeHistory
    const len = hist.length
    if (
      len >= 2 &&
      hist[len - 2].fullPath === to.fullPath &&
      JSON.stringify(hist[len - 2].params) == JSON.stringify(to.params)
    ) {
      User.getMe()
        .then((user) => {
          if (user.isAdmin) {
            const scrollY = routeHistorySingleton.getScroll(to.fullPath) || 0
            routeHistorySingleton.pop()
            restoreScrollWithUseScroll(scrollY, { timeoutMs: 10000, pollInterval: 120 })
            return
          }
        })
        .catch(() => {})
    } else {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 750)
    }
  })

  const historyMixin = {
    data() {
      return {
        routeHistory: routeHistorySingleton.routeHistory
      }
    }
  }

  return {
    historyMixin,
    restoreScrollWithUseScroll,
    componentState,
    saveState,
    getState,
    routeHistorySingleton
  }
}
